<template>
  <main class="jp container">
    <template v-if="isValidJob">
      <job-meta :job="job" />
      <breadcrumbs :value="breadcrumbs" />
      <job-page-wrapper
        :job="job"
        :company-jobs="companyJobs"
        :company-jobs-total="companyJobsTotal"
        :bidding-id="lastBiddingId"
        :bidding-session="biddingSession"
        :request-id="requestId"
        :tracking-params="lastTrackingParams"
        :related-jobs="relatedJobs"
        :related-jobs-total="relatedJobsTotal"
        :related-jobs-without-ads-total="relatedJobsWithoutAds.length"
        :session="session"
        @related-jobs-wrapper-loading="getJobsBasedOnTab"
      />
    </template>
    <error-content v-if="isShowError" :job-id="jobID" />
  </main>
</template>

<script>
import {
  GET_JOBS_BY_IDS_QUERY,
  GET_RELATED_AND_COMPANY_JOBS_BY_IDS_QUERY,
  GET_RELATED_JOBS_BY_IDS_QUERY,
  QUERY_JOBS,
} from '../../api/octo/graphql/queries'
import { interleave, sliceByInt } from '../../libs/data/transform'
import _get from 'lodash/get'
import _uniqBy from 'lodash/uniqBy'
import AuthMixin from '../../mixins/auth'
import BiddingMixin from '../../mixins/bidding'
import Breadcrumbs from '../nav/breadcrumbs/breadcrumbs'
import BreadcrumbsMixin from '../../mixins/breadcrumb'
import { createSessionId } from '../../libs/tracking/utils/session'
import ErrorContent from '../error/errorContent'
import { getJobLocalePaths } from '../../libs/job'
import { getTrackingSearchParams } from '../../libs/tracking/utils/event'
import GraphqlFormMixin from '../../mixins/form/graphql'
import JobMetadata from './jobMetadata'
import JobPageWrapper from './jobPageWrapper'
import LoadingMixin from '../../mixins/loading'

export default {
  components: {
    breadcrumbs: Breadcrumbs,
    'error-content': ErrorContent,
    'job-meta': JobMetadata,
    'job-page-wrapper': JobPageWrapper,
  },
  computed: {
    breadcrumbs() {
      let crumbsList = []
      // add employment type into breadcrumbs
      const source = 'job_detail.breadcrumb'
      crumbsList = crumbsList.concat(this.employmentToBreadcrumb([this.employmentType], source))
      if (this.job) {
        // add district and job type (main cat only) into breadcrumbs
        const districtIDs = _uniqBy(this.job.address, 'district_short_code').map(
          (district) => district.district_short_code,
        )
        const jobTypeShortCodes = _uniqBy(this.job.job_types, 'category')
          .map((jobType) => jobType.short_code)
          .filter((sc) => sc)
        crumbsList = crumbsList.concat(
          this.districtToBreadcrumb(districtIDs, source),
          this.jobTypeToBreadcrumb(jobTypeShortCodes, source),
        )
      }
      return crumbsList
    },
    employmentType() {
      return _get(this.job, 'employment') || ''
    },
    /**
     * Graphql variables
     */
    gqlVariables() {
      const companyID = _get(this.job, 'company._id') || ''
      let variables
      switch (this.gqlOperation) {
        case 'getRelatedAndCompanyJobs':
          variables = {
            companyIDs: [companyID],
            exclude: [this.jobID], // Exclude fetching the job in the current job page
            jobID: this.jobID,
            limit: 12,
            offset: 0,
          }
          break
        case 'getCompanyJobs':
          variables = {
            company: [companyID],
            exclude: [this.jobID],
            limit: 20,
            offset: this.companyJobs.length,
          }
          break
        case 'getRelatedJobs':
          variables = {
            jobID: this.jobID,
            limit: 20,
            offset: this.relatedJobsWithoutAds.length,
          }
          break
        case 'getJob':
          variables = {
            ids: [this.jobID],
          }
          break
        default:
          variables = {}
          break
      }
      return variables
    },
    isShowError() {
      return !this.isLoading && !this.isValidJob
    },
    isValidJob() {
      return this.job && this.job.state === 'Posted'
    },
    jobID() {
      return this.$route.params.id || ''
    },
    showRelatedLists() {
      return this.relatedJobs.length > 0 || this.companyJobs.length > 0
    },
  },
  created() {
    if (!this.job && this.jobID) {
      // SPA mode
      this.openLoadingDialog()
      this.$tokenReady(async (token) => {
        await this.getJob(this.jobID)
        await this.getRelatedAndCompanyJobsWithAds()
        this.closeLoadingDialog()
      })
    } else if (this.job) {
      // static payload mode
      // or ssg mode
      if (this.job._id !== this.jobID) {
        // actually, I don't know this code path will even reach
        this.openLoadingDialog()
        this.$tokenReady(async (token) => {
          await this.getJob(this.jobID)
          await this.getRelatedAndCompanyJobsWithAds()
          this.closeLoadingDialog()
        })
      } else {
        this.$tokenReady(async (token) => {
          // refresh user-specific flags
          // TODO Only fetch user-specific flags
          if (this.jobID) {
            await this.getJob(this.jobID, false)
          }
          await this.getRelatedAndCompanyJobsWithAds()
        })
      }
    } else {
    }
  },
  data() {
    return {
      companyJobs: [],
      companyJobsTotal: 0,
      /**
       * Graphql queries
       */
      gqlQueries: {
        getCompanyJobs: QUERY_JOBS,
        getJob: GET_JOBS_BY_IDS_QUERY,
        getRelatedAndCompanyJobs: GET_RELATED_AND_COMPANY_JOBS_BY_IDS_QUERY,
        getRelatedJobs: GET_RELATED_JOBS_BY_IDS_QUERY,
      },
      job: this.jobSsr,
      jobCardPerBatch: 6,
      /** Record for tracking params of last search */
      lastTrackingParams: null,
      /** Only show the top ads section */
      relatedJobs: [],
      relatedJobsTotal: 0,
      relatedJobsWithoutAds: [],
      session: {
        company: '',
        related: '',
      },
    }
  },
  layout: 'defaultNoQuickBar',
  methods: {
    async getCompanyJobs() {
      const companyJobsRes = await this.submitGql('getCompanyJobs', true)
      if (companyJobsRes && companyJobsRes.data && companyJobsRes.data.job_search) {
        this.companyJobs = this.companyJobs.concat(companyJobsRes.data.job_search.result)
      }
    },
    /**
     * Get job from *Octo* by job ID
     */
    async getJob(jobID) {
      // Fetch job from Octo
      const res = await this.submitGql('getJob', true)
      if (res && res.data && Array.isArray(res.data.get_jobs)) {
        const job = res.data.get_jobs[0]
        if (job && job._id === jobID) {
          // Set job from result
          this.job = job

          // Set localised path mapping if not available yet
          if (!this.currentPageLocalePaths) {
            this.$store.commit('locale/setPageLocalePaths', getJobLocalePaths(job))
          }
        }
      }
    },
    /**
     * Request bidding to get job ads
     */
    getJobAds() {
      if (this.job) {
        const employment = [this.job.employment]
        const district = Array.isArray(this.job.address)
          ? this.job.address.map((address) => address.district_short_code)
          : []
        const jobType = Array.isArray(this.job.job_types)
          ? this.job.job_types.map((type) => type.short_code).filter((sc) => sc)
          : []
        const filterQuery = { district, employment, jobType }
        this.lastTrackingParams = getTrackingSearchParams(filterQuery)
        return this.loadAds('job_detail.related_job', filterQuery, [this.job._id])
      }
    },
    async getJobsBasedOnTab(tab) {
      switch (tab) {
        case 'company': {
          await this.getCompanyJobs()
          break
        }
        case 'related': {
          await this.getRelatedJobsWithAds()
          break
        }
      }
    },
    async getRelatedAndCompanyJobs() {
      const res = await this.submitGql('getRelatedAndCompanyJobs', true)
      if (res && res.data && res.data.related_jobs && res.data.company_jobs) {
        // Set related jobs list
        this.relatedJobsTotal = res.data.related_jobs.total
        const relatedJobs = res.data.related_jobs.result
        this.relatedJobsWithoutAds = this.relatedJobsWithoutAds.concat(relatedJobs)
        this.session.related = createSessionId()

        // Set company jobs list
        this.companyJobs = this.companyJobs.concat(res.data.company_jobs.result)
        this.companyJobsTotal = res.data.company_jobs.total
        this.session.company = createSessionId()
      }
    },
    // FIXME: The user cannot get back to the current list position,
    // if he clicks on a job post in the related or company job list and goes to a new job detail page,
    // but clicks on the back button to get back to current job page
    async getRelatedAndCompanyJobsWithAds() {
      await Promise.all([this.getRelatedAndCompanyJobs(), this.getJobAds()])

      const relatedJobsWithoutAdsPages = sliceByInt(this.relatedJobsWithoutAds, [this.jobCardPerBatch], true)
      this.relatedJobs = interleave(relatedJobsWithoutAdsPages, this.biddingAdsPages)
    },
    /**
     * Get related from *Octo*
     * by job
     * return related jobs
     */
    // FIXME: We may fetch duplicate jobs because of using offset as an variable
    // The position of each related job may change in the backend,
    // so using offset as an variable may get duplicate job.
    async getRelatedJobsAndTotal() {
      const res = await this.submitGql('getRelatedJobs', true)
      if (res && res.data.related_jobs && res.data.related_jobs.result) {
        // Since the total in related_jobs returned by the GraphQL call may be different every time
        // we should update the total after we fetch the total of related jobs
        this.relatedJobsTotal = res.data.related_jobs.total
        const relatedJobs = res.data.related_jobs.result
        this.relatedJobsWithoutAds = this.relatedJobsWithoutAds.concat(relatedJobs)
      }
    },
    async getRelatedJobsWithAds() {
      await this.getRelatedJobsAndTotal()
      const relatedJobsWithoutAdsPages = sliceByInt(this.relatedJobsWithoutAds, [this.jobCardPerBatch], true)
      this.relatedJobs = interleave(relatedJobsWithoutAdsPages, this.biddingAdsPages)
    },
  },
  mixins: [AuthMixin, BreadcrumbsMixin, BiddingMixin, GraphqlFormMixin, LoadingMixin],
  name: 'JobPage',
  props: {
    jobSsr: {
      default: null,
      type: Object,
    },
  },
}
</script>

<style lang="postcss" scoped></style>
